/**
 * Room Assignment Service
 * Handles automatic assignment of elements (fixtures, controls, LV products) to rooms
 * based on their positions and room polygons
 */

import { logger } from '@/lib/logger';
import { isPointInPolygon } from '@/utils/roomProcessing';
import { updateFixture, getFixtures } from './fixtureService';
import { updateLightingControl, getLightingControlsByFloorPlan } from './lightingControlService';
import { lvProductService } from '@/features/product-library/services/lvProductService';
import { getRooms } from './roomService';
import type { Room } from '@/types/floorPlanViewer';
import type { LightingFixture } from '@/types/floorPlanViewer';
import type { LightingControl } from '@/types/lighting';
import type { LVProduct } from '@/features/product-library/types';

export interface RoomAssignmentResult {
  total: number;
  fixtures: number;
  controls: number;
  lvProducts: number;
  errors: string[];
}

/**
 * Find which elements are inside a specific room
 */
function getElementsInRoom(
  room: Room,
  fixtures: LightingFixture[],
  controls: LightingControl[],
  lvProducts: LVProduct[],
): {
  fixtures: string[];
  controls: string[];
  lvProducts: string[];
} {
  const result = {
    fixtures: [] as string[],
    controls: [] as string[],
    lvProducts: [] as string[],
  };

  const roomPolygon = room.points;

  // Check fixtures
  fixtures.forEach(fixture => {
    // Only assign if fixture doesn't already have a room
    if (!fixture.room_id) {
      const fixturePoint = { x: fixture.x_position, y: fixture.y_position };
      if (isPointInPolygon(fixturePoint, roomPolygon)) {
        result.fixtures.push(fixture.id);
      }
    }
  });

  // Check controls
  controls.forEach(control => {
    // Only assign if control doesn't already have a room
    if (!control.roomId) {
      const controlPoint = { x: control.xPosition, y: control.yPosition };
      if (isPointInPolygon(controlPoint, roomPolygon)) {
        result.controls.push(control.id);
      }
    }
  });

  // Check LV products
  lvProducts.forEach(product => {
    // Only assign if product doesn't already have a room
    if (!product.room_id) {
      const productPoint = { x: product.x_position, y: product.y_position };
      if (isPointInPolygon(productPoint, roomPolygon)) {
        result.lvProducts.push(product.id);
      }
    }
  });

  return result;
}

/**
 * Assign all elements to their corresponding rooms based on position
 * Only updates elements that don't already have a room assigned
 * Only considers rooms that meet the confidence threshold
 *
 * @param rooms - Array of room polygons
 * @param fixtures - Array of fixtures to potentially assign
 * @param controls - Array of controls to potentially assign
 * @param lvProducts - Array of LV products to potentially assign
 * @param confidenceThreshold - Minimum confidence score (0-1) for room to be considered. Defaults to 0 (all rooms).
 * @returns Statistics about assignments made
 */
export async function assignElementsToRooms(
  rooms: Room[],
  fixtures: LightingFixture[],
  controls: LightingControl[],
  lvProducts: LVProduct[],
  confidenceThreshold: number = 0,
): Promise<RoomAssignmentResult> {
  const result: RoomAssignmentResult = {
    total: 0,
    fixtures: 0,
    controls: 0,
    lvProducts: 0,
    errors: [],
  };

  if (!rooms || rooms.length === 0) {
    logger.log('No rooms to assign elements to');
    return result;
  }

  // Filter rooms by confidence threshold (same as RoomLayer rendering logic)
  const visibleRooms = rooms.filter(room => {
    const confidence = room.confidence ?? 1; // AI detected rooms have confidence, manual rooms default to 1
    return confidence >= confidenceThreshold;
  });

  logger.log(
    `Starting element assignment for ${visibleRooms.length}/${rooms.length} rooms (threshold: ${confidenceThreshold}), ${fixtures.length} fixtures, ${controls.length} controls, ${lvProducts.length} LV products`,
  );

  // Process each visible room
  for (const room of visibleRooms) {
    logger.log(`Processing room ${room.id} (${room.name})`);

    const elementsInRoom = getElementsInRoom(room, fixtures, controls, lvProducts);

    // Update fixtures
    if (elementsInRoom.fixtures.length > 0) {
      logger.log(`Assigning ${elementsInRoom.fixtures.length} fixtures to room ${room.name}`);
      for (const fixtureId of elementsInRoom.fixtures) {
        try {
          await updateFixture(fixtureId, { room_id: room.id });
          result.fixtures++;
        } catch (error: any) {
          // Handle race condition where fixture was deleted or updated concurrently
          // Check multiple possible error structures from Supabase
          const statusCode = error?.status || error?.code || error?.statusCode;
          const isConflictError =
            statusCode === 409 || statusCode === '409' || error?.message?.includes('409');
          const isNotAcceptableError =
            statusCode === 406 || statusCode === '406' || error?.message?.includes('406');
          const isRaceCondition = isConflictError || isNotAcceptableError;

          if (isRaceCondition) {
            logger.debug(
              `Fixture ${fixtureId} was deleted or updated concurrently during assignment - skipping (status: ${statusCode})`,
            );
            // Don't treat this as an error - it's expected in concurrent scenarios
          } else {
            const errorMsg = `Failed to assign fixture ${fixtureId} to room ${room.id}: ${error}`;
            logger.error(errorMsg);
            logger.debug('Error details:', {
              statusCode,
              errorCode: error?.code,
              errorStatus: error?.status,
              message: error?.message,
            });
            result.errors.push(errorMsg);
          }
        }
      }
    }

    // Update controls
    if (elementsInRoom.controls.length > 0) {
      logger.log(`Assigning ${elementsInRoom.controls.length} controls to room ${room.name}`);
      for (const controlId of elementsInRoom.controls) {
        try {
          await updateLightingControl(controlId, { roomId: room.id });
          result.controls++;
        } catch (error: any) {
          // Handle race condition where control was deleted or updated concurrently
          // Check multiple possible error structures from Supabase
          const statusCode = error?.status || error?.code || error?.statusCode;
          const isConflictError =
            statusCode === 409 || statusCode === '409' || error?.message?.includes('409');
          const isNotAcceptableError =
            statusCode === 406 || statusCode === '406' || error?.message?.includes('406');
          const isRaceCondition = isConflictError || isNotAcceptableError;

          if (isRaceCondition) {
            logger.debug(
              `Control ${controlId} was deleted or updated concurrently during assignment - skipping (status: ${statusCode})`,
            );
            // Don't treat this as an error - it's expected in concurrent scenarios
          } else {
            const errorMsg = `Failed to assign control ${controlId} to room ${room.id}: ${error}`;
            logger.error(errorMsg);
            result.errors.push(errorMsg);
          }
        }
      }
    }

    // Update LV products
    if (elementsInRoom.lvProducts.length > 0) {
      logger.log(`Assigning ${elementsInRoom.lvProducts.length} LV products to room ${room.name}`);
      for (const productId of elementsInRoom.lvProducts) {
        try {
          await lvProductService.update(productId, { room_id: room.id });
          result.lvProducts++;
        } catch (error: any) {
          // Handle race condition where LV product was deleted or updated concurrently
          // Check multiple possible error structures from Supabase
          const statusCode = error?.status || error?.code || error?.statusCode;
          const isConflictError =
            statusCode === 409 || statusCode === '409' || error?.message?.includes('409');
          const isNotAcceptableError =
            statusCode === 406 || statusCode === '406' || error?.message?.includes('406');
          const isRaceCondition = isConflictError || isNotAcceptableError;

          if (isRaceCondition) {
            logger.debug(
              `LV product ${productId} was deleted or updated concurrently during assignment - skipping (status: ${statusCode})`,
            );
            // Don't treat this as an error - it's expected in concurrent scenarios
          } else {
            const errorMsg = `Failed to assign LV product ${productId} to room ${room.id}: ${error}`;
            logger.error(errorMsg);
            result.errors.push(errorMsg);
          }
        }
      }
    }
  }

  result.total = result.fixtures + result.controls + result.lvProducts;

  logger.log(
    `Element assignment complete: ${result.total} total (${result.fixtures} fixtures, ${result.controls} controls, ${result.lvProducts} LV products)`,
  );

  if (result.errors.length > 0) {
    logger.warn(`${result.errors.length} errors occurred during assignment`);
  }

  return result;
}

/**
 * Assign elements to rooms by querying fresh data from the database
 * This ensures we have the most up-to-date room and element data
 * Only considers rooms that meet the confidence threshold
 * Only assigns fixtures that meet the fixture confidence threshold (same logic as UI visibility)
 *
 * @param floorPlanId - The floor plan ID to query data for
 * @param roomConfidenceThreshold - Minimum confidence score (0-1) for room to be considered. Defaults to 0 (all rooms).
 * @param fixtureConfidenceThreshold - Minimum confidence score (0-1) for fixtures to be assigned. Defaults to 0.3.
 * @returns Statistics about assignments made
 */
export async function assignElementsToRoomsByFloorPlan(
  floorPlanId: string,
  roomConfidenceThreshold: number = 0,
  fixtureConfidenceThreshold: number = 0.3,
): Promise<RoomAssignmentResult> {
  logger.log(
    `Fetching fresh data for floor plan ${floorPlanId} to assign elements to rooms (room threshold: ${roomConfidenceThreshold}, fixture threshold: ${fixtureConfidenceThreshold})`,
  );

  try {
    // Fetch fresh data from the database
    const [rooms, fixtures, controlsResult, lvProducts] = await Promise.all([
      getRooms(floorPlanId),
      getFixtures(floorPlanId),
      getLightingControlsByFloorPlan(floorPlanId),
      lvProductService.getByFloorPlan(floorPlanId),
    ]);

    // Extract controls from the result object
    const controls = controlsResult.data || [];

    // Filter fixtures to only include visible ones (same logic as FixtureLayer and reassignElementsBasedOnConfidence)
    const visibleFixtures = fixtures.filter(fixture => {
      // Manually placed fixtures (confidence_score === 1 or user_verified) always show
      const isManuallyPlaced = fixture.confidence_score === 1 || fixture.user_verified === true;

      if (isManuallyPlaced) {
        return true; // Always include manually placed fixtures
      }

      // For AI-detected fixtures, apply confidence threshold
      const isAIDetected = fixture.confidence_score != null && fixture.confidence_score < 1;
      if (isAIDetected) {
        const confidence = fixture.confidence_score || 0;
        return confidence >= fixtureConfidenceThreshold;
      }

      return true; // Include fixtures without confidence_score
    });

    logger.log(
      `Fetched: ${rooms.length} rooms, ${visibleFixtures.length}/${fixtures.length} fixtures (visible), ${controls.length} controls, ${lvProducts.length} LV products`,
    );

    // Call the main assignment function with fresh data and confidence threshold
    // Only assign visible fixtures, but all controls and LV products
    return await assignElementsToRooms(
      rooms,
      visibleFixtures,
      controls,
      lvProducts,
      roomConfidenceThreshold,
    );
  } catch (error) {
    logger.error('Error fetching data for room assignment:', error);
    return {
      total: 0,
      fixtures: 0,
      controls: 0,
      lvProducts: 0,
      errors: [`Failed to fetch data: ${error}`],
    };
  }
}

/**
 * Reassign ALL elements based on the current confidence threshold
 * This is used when the confidence threshold changes to dynamically update room assignments
 *
 * - Elements in rooms below the threshold will have their room_id set to null
 * - Elements in spaces where rooms become visible will be assigned to those rooms
 * - Only visible elements are reassigned (respects fixture confidence threshold)
 *
 * @param floorPlanId - The floor plan ID to reassign elements for
 * @param roomConfidenceThreshold - Current room confidence threshold (0-1)
 * @param fixtureConfidenceThreshold - Current fixture confidence threshold (0-1) for AI-detected fixtures
 * @returns Statistics about reassignments made
 */
export async function reassignElementsBasedOnConfidence(
  floorPlanId: string,
  roomConfidenceThreshold: number,
  fixtureConfidenceThreshold: number = 0.3,
): Promise<RoomAssignmentResult> {
  logger.log(
    `Reassigning elements based on confidence thresholds (rooms: ${roomConfidenceThreshold}, fixtures: ${fixtureConfidenceThreshold}) for floor plan ${floorPlanId}`,
  );

  const result: RoomAssignmentResult = {
    total: 0,
    fixtures: 0,
    controls: 0,
    lvProducts: 0,
    errors: [],
  };

  try {
    // Fetch fresh data from the database
    const [rooms, fixtures, controlsResult, lvProducts] = await Promise.all([
      getRooms(floorPlanId),
      getFixtures(floorPlanId),
      getLightingControlsByFloorPlan(floorPlanId),
      lvProductService.getByFloorPlan(floorPlanId),
    ]);

    const controls = controlsResult.data || [];

    // Filter fixtures to only include visible ones (same logic as FixtureLayer)
    const visibleFixtures = fixtures.filter(fixture => {
      // Manually placed fixtures (confidence_score === 1 or user_verified) always show
      const isManuallyPlaced = fixture.confidence_score === 1 || fixture.user_verified === true;

      if (isManuallyPlaced) {
        return true; // Always include manually placed fixtures
      }

      // For AI-detected fixtures, apply confidence threshold
      const isAIDetected = fixture.confidence_score != null && fixture.confidence_score < 1;
      if (isAIDetected) {
        const confidence = fixture.confidence_score || 0;
        return confidence >= fixtureConfidenceThreshold;
      }

      return true; // Include fixtures without confidence_score
    });

    logger.log(
      `Reassigning: ${visibleFixtures.length}/${fixtures.length} fixtures (visible), ${controls.length} controls, ${lvProducts.length} LV products`,
    );

    // Filter rooms by confidence threshold
    const visibleRooms = rooms.filter(room => {
      const confidence = room.confidence ?? 1;
      return confidence >= roomConfidenceThreshold;
    });

    logger.log(
      `${visibleRooms.length}/${rooms.length} rooms are visible at threshold ${roomConfidenceThreshold}`,
    );

    // Reassign fixtures (only visible ones)
    for (const fixture of visibleFixtures) {
      try {
        const fixturePoint = { x: fixture.x_position, y: fixture.y_position };

        // Find which visible room contains this fixture
        let targetRoomId: string | null = null;
        for (const room of visibleRooms) {
          if (isPointInPolygon(fixturePoint, room.points)) {
            targetRoomId = room.id;
            break;
          }
        }

        // Only update if room assignment changed
        if (fixture.room_id !== targetRoomId) {
          await updateFixture(fixture.id, { room_id: targetRoomId });
          result.fixtures++;
          logger.log(
            `Fixture ${fixture.id} reassigned: ${fixture.room_id || 'none'} -> ${targetRoomId || 'none'}`,
          );
        }
      } catch (error: any) {
        // Handle race condition where fixture was deleted during reassignment
        const isConflictError = error?.code === '409' || error?.status === 409;
        const isNotAcceptableError = error?.code === '406' || error?.status === 406;
        const isDeletedFixture = isConflictError || isNotAcceptableError;

        if (isDeletedFixture) {
          logger.debug(
            `Fixture ${fixture.id} was deleted or is no longer accessible during reassignment - skipping`,
          );
          // Don't treat this as an error - it's expected in concurrent delete scenarios
        } else {
          const errorMsg = `Failed to reassign fixture ${fixture.id}: ${error}`;
          logger.error(errorMsg);
          result.errors.push(errorMsg);
        }
      }
    }

    // Reassign controls
    for (const control of controls) {
      try {
        const controlPoint = { x: control.xPosition, y: control.yPosition };

        let targetRoomId: string | null = null;
        for (const room of visibleRooms) {
          if (isPointInPolygon(controlPoint, room.points)) {
            targetRoomId = room.id;
            break;
          }
        }

        // Only update if room assignment changed
        if (control.roomId !== targetRoomId) {
          await updateLightingControl(control.id, { roomId: targetRoomId });
          result.controls++;
          logger.log(
            `Control ${control.id} reassigned: ${control.roomId || 'none'} -> ${targetRoomId || 'none'}`,
          );
        }
      } catch (error: any) {
        // Handle race condition where control was deleted during reassignment
        const isConflictError = error?.code === '409' || error?.status === 409;
        const isNotAcceptableError = error?.code === '406' || error?.status === 406;
        const isDeletedControl = isConflictError || isNotAcceptableError;

        if (isDeletedControl) {
          logger.debug(
            `Control ${control.id} was deleted or is no longer accessible during reassignment - skipping`,
          );
          // Don't treat this as an error - it's expected in concurrent delete scenarios
        } else {
          const errorMsg = `Failed to reassign control ${control.id}: ${error}`;
          logger.error(errorMsg);
          result.errors.push(errorMsg);
        }
      }
    }

    // Reassign LV products
    for (const product of lvProducts) {
      try {
        const productPoint = { x: product.x_position, y: product.y_position };

        let targetRoomId: string | null = null;
        for (const room of visibleRooms) {
          if (isPointInPolygon(productPoint, room.points)) {
            targetRoomId = room.id;
            break;
          }
        }

        // Only update if room assignment changed
        if (product.room_id !== targetRoomId) {
          await lvProductService.update(product.id, { room_id: targetRoomId });
          result.lvProducts++;
          logger.log(
            `LV Product ${product.id} reassigned: ${product.room_id || 'none'} -> ${targetRoomId || 'none'}`,
          );
        }
      } catch (error: any) {
        // Handle race condition where product was deleted during reassignment
        const isConflictError = error?.code === '409' || error?.status === 409;
        const isNotAcceptableError = error?.code === '406' || error?.status === 406;
        const isDeletedProduct = isConflictError || isNotAcceptableError;

        if (isDeletedProduct) {
          logger.debug(
            `LV Product ${product.id} was deleted or is no longer accessible during reassignment - skipping`,
          );
          // Don't treat this as an error - it's expected in concurrent delete scenarios
        } else {
          const errorMsg = `Failed to reassign LV product ${product.id}: ${error}`;
          logger.error(errorMsg);
          result.errors.push(errorMsg);
        }
      }
    }

    result.total = result.fixtures + result.controls + result.lvProducts;

    logger.log(
      `Reassignment complete: ${result.total} elements updated (${result.fixtures} fixtures, ${result.controls} controls, ${result.lvProducts} LV products)`,
    );

    if (result.errors.length > 0) {
      logger.warn(`${result.errors.length} errors occurred during reassignment`);
    }

    return result;
  } catch (error) {
    logger.error('Error during confidence-based reassignment:', error);
    return {
      total: 0,
      fixtures: 0,
      controls: 0,
      lvProducts: 0,
      errors: [`Failed to reassign elements: ${error}`],
    };
  }
}
